/* Cart free-shipping upsell — accordion + carousel.
 *
 * Open state is an in-memory flag for the current drawer/page session only:
 * - Starts collapsed on first open / page load
 * - Stays open across Section Rendering swaps after expand or add-from-list
 * - Cleared when the cart drawer closes (and on full page reload automatically)
 */
if (!customElements.get('cart-recommendations')) {
  let keepOpenForSession = false;

  class CartRecommendations extends HTMLElement {
    static clearKeepOpen() {
      keepOpenForSession = false;
    }

    static setKeepOpen(open) {
      keepOpenForSession = Boolean(open);
    }

    static shouldKeepOpen() {
      return keepOpenForSession;
    }

    connectedCallback() {
      this.details = this.querySelector('.cart-recommendations__details');
      this.track = this.querySelector('[data-cart-recommendations-track]');
      this.prevButton = this.querySelector('[data-cart-recommendations-prev]');
      this.nextButton = this.querySelector('[data-cart-recommendations-next]');

      if (this.details) {
        if (CartRecommendations.shouldKeepOpen()) {
          this.details.open = true;
        }

        this.onToggle = () => {
          if (this.details.open) {
            CartRecommendations.setKeepOpen(true);
            requestAnimationFrame(() => this.updateButtons());
          } else {
            CartRecommendations.setKeepOpen(false);
          }
        };
        this.details.addEventListener('toggle', this.onToggle);

        if (this.details.open) {
          requestAnimationFrame(() => this.updateButtons());
        }
      }

      // Keep open across the re-render that follows an add from this list.
      this.onAddIntent = () => CartRecommendations.setKeepOpen(true);
      this.addEventListener('submit', this.onAddIntent, true);
      this.addEventListener('click', this.onQuickAddClick, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const itemCount = event?.cartData?.item_count;
          if (typeof itemCount === 'number' && itemCount === 0) {
            CartRecommendations.clearKeepOpen();
          }
        });
      }

      if (this.track && this.prevButton && this.nextButton) {
        this.prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollByStep(-1);
        });
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollByStep(1);
        });
        this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
        this.onResize = () => this.updateButtons();
        window.addEventListener('resize', this.onResize, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.details && this.onToggle) {
        this.details.removeEventListener('toggle', this.onToggle);
      }
      if (this.onAddIntent) {
        this.removeEventListener('submit', this.onAddIntent, true);
      }
      if (this.onQuickAddClick) {
        this.removeEventListener('click', this.onQuickAddClick, true);
      }
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    onQuickAddClick = (event) => {
      const submit = event.target.closest('button[type="submit"][name="add"], .quick-add__submit');
      if (!submit || !this.contains(submit)) return;
      CartRecommendations.setKeepOpen(true);
    };

    scrollByStep(direction) {
      if (!this.track) return;
      const card = this.track.querySelector('.cart-recommendation-card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 12;
      const step = card.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.track || !this.prevButton || !this.nextButton) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
      this.prevButton.toggleAttribute('disabled', atStart);
      this.nextButton.toggleAttribute('disabled', atEnd);
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
  window.CartRecommendations = CartRecommendations;
}
